import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik, getIn, useFormikContext } from 'formik';
import _, { isEmpty, snakeCase } from 'lodash';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import RuleConditionsQueryBuilder, {
  hasUnknownFieldPath,
  UNKNOWN_FIELD_SAVE_BLOCKED,
  validateConditionsTree,
} from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder';

import { ASSIGNMENTS_MIXPANEL_EVENTS } from '../../../../pocs/mixpanel';
import {
  ASSIGNMENT_RULE_ENTITY_DICT,
  ASSIGNMENT_RULE_METHOD_DICT,
  CONFIGURATION_FEATURES_NAMES,
} from '../../../../Types';
import { isFeatureEnabled, reportAxiosError, subOrgIdToNameDict } from '../../../../Utils';
import { getLobDescription } from '../../../../Utils/lobUtils';
import CardDialog from '../../../CardDialog';
import mixpanel from '../../../CmsMain/mixpanel';
import WithConfirm from '../../../ConfirmModal';
import { Heading, LoadingSwitch, SortableTable } from '../../../core';
import InnerTabs from '../../../core/Layout/InnerTabs/InnerTabs';
import {
  ArrowDownwardIcon,
  ArrowUpwardIcon,
  InfoIcon,
  PencilIcon,
  RemoveIcon,
  TrashIcon_Deprecated,
} from '../../../icons';
import InlineIconButton from '../../../InlineIconButton';
import LoadingIndicator from '../../../LoadingIndicator';
import OverflowTextWithToolTip from '../../../OverflowTextWithToolTip';
import { MultiSelectTextFieldFormik, TextFieldFormik, useSetDefaultFieldsOnChange } from '../../../TextFieldFormik';
import LobRadioFormik from '../../../TPA/LOB/LobRadioFormik';
import SubOrganizationMultiselectFormik from '../../../TPA/SubOrganizations/SubOrganizationMultiselectFormik';
import useDataFetcher from '../../../useDataFetcher';
import useAssignments, { AssignmentsContextProvider } from '../../AssignmentsContext';
import OperationsBreadcrumbs from '../../OperationsBreadcrumbs';
import { useSysconfig } from '../../SystemConfigurationScreen';

import MoiAssignmentRulesTab from './MoiRules/MoiAssignmentRulesTab';
import { TEXTS as MOI_RULES_TEXTS } from './MoiRules/texts';
import { AssignmentDefaultsControls } from './AssignmentDefaultsControls';

import { useStyles } from '../../../../assets/styles';
import styles from './AssignmentsTab.module.scss';

const EnableAssignmentsButton = ({ onAssignmentsEnabled }) => {
  const { organization } = useSysconfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const enableAssignments = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(`/sysconfig/api/v1/organizations/${organization.id}/assignments/enable`);
      await onAssignmentsEnabled();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Button
      color="primary"
      variant="contained"
      style={{ marginLeft: 24 }}
      disabled={isSubmitting}
      onClick={enableAssignments}
    >
      Enable Assignments
    </Button>
  );
};

EnableAssignmentsButton.propTypes = {
  onAssignmentsEnabled: PropTypes.func.isRequired,
};

const AssignmentsTab = () => {
  const { organization } = useSysconfig();

  const Header = () => (
    <>
      {!isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.AUTOMATIC_EXPOSURES_RULES) && (
        <>
          <OperationsBreadcrumbs currentTab="Assignment Configuration" />
          <Heading variant={Heading.TYPES.H2}>General Assignments Configuration</Heading>
        </>
      )}
    </>
  );

  return (
    <>
      <CardDialog noCardTitle>
        <Header />
        <AssignmentsContextProvider>
          <AssignmentsTabInner />
        </AssignmentsContextProvider>
      </CardDialog>
    </>
  );
};

const AssignmentsTabSkeleton = () => <SkeletonTable rowsCount={5} columnsCount={5} />;

const AssignmentsTabInner = () => {
  const { organization } = useSysconfig();
  const { areAssignmentsEnabled, isLoading, isError, reloadDefaults, reloadEnabled } = useAssignments();
  const isMoiRulesEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.MOI_ASSIGNMENT_RULES);

  // Claim rules and exposure rules are separate ordered lists on the server — each is only ever
  // evaluated for its own entity — so each gets its own tab and its own ordering.
  const claimAssignmentTabs = [
    { label: 'Claim Rules', url: 'claim-rules', component: AssignmentRulesTab, props: { entity: 'claims' } },
    {
      label: 'Exposure Rules',
      url: 'exposure-rules',
      component: AssignmentRulesTab,
      props: { entity: 'exposures' },
    },
    { label: 'Groups', url: 'groups', component: AssignmentGroupsTab },
  ];
  // MOI rules choose an inspection's vendor, and resolving them never checks whether claim
  // assignment is enabled, so their tab does not wait for it either.
  const tabs = [
    ...(areAssignmentsEnabled ? claimAssignmentTabs : []),
    ...(isMoiRulesEnabled
      ? [{ label: MOI_RULES_TEXTS.TAB_LABEL, url: 'moi-rules', component: MoiAssignmentRulesTab }]
      : []),
  ];

  const handleAssignmentsEnabled = async () => {
    try {
      await reloadDefaults();
      await reloadEnabled();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError} LoadingComponent={AssignmentsTabSkeleton}>
      <>
        {!areAssignmentsEnabled ? <EnableAssignmentsButton onAssignmentsEnabled={handleAssignmentsEnabled} /> : null}
        {areAssignmentsEnabled ? <AssignmentDefaultsControls /> : null}
        {!isEmpty(tabs) ? <InnerTabs tabs={tabs} /> : null}
      </>
    </LoadingSwitch>
  );
};

const AssignmentRulesTab = ({ entity }) => {
  const { organization } = useSysconfig();
  const { classes } = useStyles();
  const {
    isLoading,
    isError,
    data: allRules,
    reloadData: reloadRules,
  } = useDataFetcher(`/sysconfig/api/v1/organizations/${organization.id}/assignments/rules`);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState(null);
  const { lobConfigurationsDict } = useLobConfiguration();

  if (isLoading || isError) {
    return <LoadingIndicator isError={isError} />;
  }

  // The endpoint returns every rule the organization has, already ordered within each entity.
  // This tab shows one entity; `allRules` is still what the uniqueness check below runs against,
  // because rule names are unique across the whole organization, not per entity.
  const rules = allRules.filter((rule) => rule.entity === entity);

  const getMixpanelEventParams = (values) => {
    return {
      rule_name: values?.display_name,
      sub_organizations: values?.sub_organization_ids,
      lob: values?.lob,
      entity: values?.entity,
      method: values?.method,
      // Keep the analytics property primitive: the group/user id, or the sentinel kind.
      assignee: values?.assignee?.id ?? values?.assignee?.kind,
    };
  };

  const addRule = () => {
    setRuleDialogOpen(true);
    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ADD_ASSIGNMENT_RULE_CLICKED);
  };

  const handleEditRule = (rule) => {
    setRuleToEdit(rule);
    setRuleDialogOpen(true);

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.EDIT_ASSIGNMENT_RULE_CLICKED, getMixpanelEventParams(rule));
  };

  const handleDeleteRuleClicked = (rule) => {
    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.DELETE_ASSIGNMENT_RULE_CLICKED, getMixpanelEventParams(rule));
  };

  const onClose = () => {
    setRuleToEdit(null);
    setRuleDialogOpen(false);
  };

  const callAPI = async (method, url, values) => {
    await axios({ method, url, data: values });
    await reloadRules();
  };

  const callAPIWithCatch = async (method, url, values) => {
    try {
      await callAPI(method, url, values);
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handleDeleteRule = async (rule) => {
    await callAPIWithCatch('delete', `/sysconfig/api/v1/organizations/${organization.id}/assignments/rules/${rule.id}`);

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_RULE_DELETED, getMixpanelEventParams(rule));
  };

  // Ordering is sent as data: swap the rule with its neighbour and post this entity's whole order.
  const handleMoveRule = async (rule, offset, mixpanelEvent) => {
    const orderedIds = rules.map((r) => r.id);
    const index = orderedIds.indexOf(rule.id);
    const target = index + offset;
    if (index < 0 || target < 0 || target >= orderedIds.length) {
      return;
    }
    [orderedIds[index], orderedIds[target]] = [orderedIds[target], orderedIds[index]];

    await callAPIWithCatch('post', `/sysconfig/api/v1/organizations/${organization.id}/assignments/rules/reorder`, {
      entity,
      ordered_ids: orderedIds,
    });

    mixpanel.track(mixpanelEvent, getMixpanelEventParams(rule));
  };

  const handleCreateRule = async (values) => {
    await callAPI('post', `/sysconfig/api/v1/organizations/${organization.id}/assignments/rules`, values);

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_RULE_ADDED, getMixpanelEventParams(values));
  };

  const onUpdateRule = async (values, id) => {
    await callAPI('patch', `/sysconfig/api/v1/organizations/${organization.id}/assignments/rules/${id}`, values);

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_RULE_EDITED, getMixpanelEventParams(values));
  };

  const actionOptions = (rule) => [
    {
      onClick: () => handleEditRule(rule),
      node: 'Edit',
      key: 'editRule',
    },
    {
      onClick: () => handleDeleteRule(rule),
      withConfirmProps: {
        title: 'Delete Rule?',
        contentText: `Are you sure you want to delete "${rule.display_name}"?`,
        primaryButtonName: 'Delete',
      },
      onOpenConfirmModal: () => handleDeleteRuleClicked(rule),
      disabled: false,
      node: 'Delete',
      key: 'deleteRule',
    },
  ];

  const columnData = [
    { id: 'display_name', label: 'Name' },
    { id: 'lob', label: 'Line of Business', specialCell: (rule) => getLobDescription(rule.lob, lobConfigurationsDict) },
    { id: 'method', label: 'Method', specialCell: (rule) => ASSIGNMENT_RULE_METHOD_DICT[rule.method]['desc'] },
    { id: 'assignee_desc', label: 'Assignee' },
    {
      id: 'actions',
      disableSort: true,
      label: 'Actions',
      width: '90px',
      specialCell: (rule) => (
        <div className={classes.containerCentered}>
          <span style={{ marginRight: 10 }}>
            <InlineIconButton
              icon={ArrowUpwardIcon}
              ariaLabel="Move rule up"
              className={classes.hoverableIcon}
              onClick={() => handleMoveRule(rule, -1, ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_RULE_MOVED_UP_IN_ORDER)}
              disabled={rule.id === rules[0].id}
            />
          </span>
          <InlineIconButton
            icon={ArrowDownwardIcon}
            ariaLabel="Move rule down"
            className={classes.hoverableIcon}
            onClick={() => handleMoveRule(rule, 1, ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_RULE_MOVED_DOWN_ORDER)}
            disabled={rule.id === rules[rules.length - 1].id}
          />
          <ThreeDotsMenu options={actionOptions(rule)} />
        </div>
      ),
    },
  ];

  if (organization.sub_organizations_enabled) {
    columnData.splice(1, 0, {
      id: 'sub_organization_ids',
      label: 'Sub Organizations',
      specialCell: (rule) => {
        if (rule.is_all_sub_orgs) {
          return 'All';
        }

        const subOrgIdToName = subOrgIdToNameDict(organization);
        return rule.sub_organization_ids.map((subOrgId) => subOrgIdToName[subOrgId]).join(', ');
      },
    });
  }

  return (
    <Grid container direction="column">
      <div className={styles.tableBanner}>
        <Grid container direction="row" justify="space-between">
          <Grid item>
            <span style={{ display: 'flex' }}>
              <InfoIcon size={20} iconColor="#606060" />
              <Typography variant="body1" style={{ color: '#606060', paddingLeft: 5 }}>
                Rules will be applied in the order that they are presented{' '}
              </Typography>
            </span>
          </Grid>
          <Grid item>
            <Button color="primary" onClick={addRule}>
              <AddIcon />
              Add Rule
            </Button>
          </Grid>
        </Grid>
      </div>
      <Grid item xs={12}>
        <SortableTable columns={columnData} rows={rules} disableSortByUser keepRowsOrder />
      </Grid>
      {ruleDialogOpen && (
        <EditAssignmentRuleDialog
          onSubmit={ruleToEdit ? onUpdateRule : handleCreateRule}
          onClose={onClose}
          rule={ruleToEdit}
          entity={entity}
          existingRuleNames={allRules.filter((rule) => rule.id !== ruleToEdit?.id).map((rule) => rule.display_name)}
        />
      )}
    </Grid>
  );
};

AssignmentRulesTab.propTypes = {
  entity: PropTypes.oneOf(['claims', 'exposures']).isRequired,
};

/** Save, plus the one reason it is ever disabled rather than merely inert.
 *
 *  `isWaiting` covers the transient states — submitting, or the condition catalog still in
 *  flight — which need no tooltip because they resolve on their own. `blockedReason` is the
 *  standing one, and it does: a disabled MUI button swallows its own pointer events, so the
 *  tooltip hangs on a wrapper, without which the explanation never appears at all. */
const SaveRuleButton = ({ isWaiting, onClick, blockedReason }) => {
  const button = (
    <Button variant="contained" color="primary" disabled={isWaiting || !!blockedReason} onClick={onClick}>
      Save
    </Button>
  );

  // Deliberately not tooltipped while merely waiting: a tooltip that appears and disappears on
  // its own teaches the reader nothing, and the standing reason must not be claimed early —
  // mid-fetch the registry is empty and nothing is known to have drifted yet.
  if (!blockedReason) {
    return button;
  }
  return (
    <Tooltip title={blockedReason}>
      <span data-testid="assignment-rule-save-blocked">{button}</span>
    </Tooltip>
  );
};

SaveRuleButton.propTypes = {
  isWaiting: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  blockedReason: PropTypes.string,
};

const EditAssignmentRuleDialog = ({ rule, onSubmit, onClose, entity, existingRuleNames = [] }) => {
  const { classes } = useStyles();

  const {
    organization,
    organizationOperationalDetails: { supportedClaimTypes },
  } = useSysconfig();

  // The QB Formik wrapper hands us the fetched FieldDefinition list via
  // onFieldsLoaded; we hold it here so the form-level `validate` prop below
  // can walk values.conditions_json against the same registry.
  const [conditionFields, setConditionFields] = useState([]);
  // Mid-fetch the registry is `[]`, which reads exactly like "this scope has no fields" — so a
  // drifted leaf looks resolvable and `validate` passes. Save waits for the answer rather than
  // briefly offering a submit the loaded catalog would reject.
  const [isLoadingConditionFields, setIsLoadingConditionFields] = useState(false);

  return (
    <Formik
      initialValues={{
        id: rule?.id || '',
        display_name: rule?.display_name || '',
        is_all_sub_orgs: rule?.is_all_sub_orgs || false,
        sub_organization_ids: rule?.sub_organization_ids || [],
        lob: rule?.lob || '',
        // The tab's entity, not a choice: a rule belongs to the list it was created in, and the
        // server rejects an update that names a different one. Submitted because create needs it.
        entity,
        method: rule?.method || '',
        // The tagged `{kind, id}` the API speaks; `null` when unset so `required` rejects it
        // and the single-option auto-select still fires.
        assignee: rule?.assignee ?? null,
        // Opaque RQB JSON tree — the only conditions representation for assignment rules.
        conditions_json: rule?.conditions_json || null,
      }}
      validationSchema={Yup.object().shape({
        display_name: Yup.string().required('Required').notOneOf(existingRuleNames, 'Must be unique'),
        is_all_sub_orgs: Yup.bool(),
        sub_organization_ids: Yup.array(),
        lob: Yup.string().required('Required').oneOf(supportedClaimTypes, 'Invalid line of business'),
        entity: Yup.string().required('Required').oneOf(Object.keys(ASSIGNMENT_RULE_ENTITY_DICT), 'Invalid entity'),
        method: Yup.string().required('Required').oneOf(Object.keys(ASSIGNMENT_RULE_METHOD_DICT), 'Invalid method'),
        assignee: Yup.mixed().required('Required'),
      })}
      enableReinitialize
      validate={(values) => {
        const errors = {};
        const treeError = validateConditionsTree(values.conditions_json, conditionFields);
        if (treeError) {
          errors.conditions_json = treeError;
        }
        return errors;
      }}
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values, rule?.id);
          onClose();
        } catch (error) {
          await reportAxiosError(error);
          formikProps.setSubmitting(false);
        }
      }}
    >
      {(formikProps) => {
        const { isSubmitting, handleSubmit, values } = formikProps;

        return (
          <CardDialog
            isDialog
            title={rule ? 'Edit Rule' : 'Add Rule'}
            fullWidth
            maxWidth="md"
            onClose={onClose}
            preventClose={isSubmitting}
          >
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <div>
                  <TextFieldFormik id="display_name" label="Rule Name" className={classes.formTextField} fullWidth />
                </div>
              </Grid>
              <Grid item xs={6} />
              {organization.sub_organizations_enabled && (
                <>
                  <Grid item xs={6} style={{ marginBottom: 24 }}>
                    <SubOrganizationMultiselectFormik
                      subOrganizationsFieldId="sub_organization_ids"
                      allSelectedFieldId="is_all_sub_orgs"
                      selectAllLabel="All (inc. future sub-orgs)"
                      showAllInRenderedSelectedOptions
                      shouldDisplayAllOption
                    />
                  </Grid>
                  <Grid item xs={6} />
                </>
              )}
              <Grid item xs={8}>
                <div style={{ marginBottom: 24 }}>
                  <Typography
                    display="block"
                    variant="subtitle2"
                    style={{ marginBottom: 10, fontWeight: 'bold', color: '#202020' }}
                  >
                    Line of Business
                  </Typography>
                  <LobRadioFormik
                    lobFieldId="lob"
                    subOrganizationIds={
                      values.sub_organization_ids.length
                        ? values.sub_organization_ids
                        : organization.sub_organizations.map((so) => so.id)
                    }
                  />
                </div>
              </Grid>
              <Grid item xs={4} />
              <Grid item xs={6}>
                <div>
                  <TextFieldFormik id="method" label="Method" className={classes.formTextField} fullWidth select>
                    {Object.keys(ASSIGNMENT_RULE_METHOD_DICT).map((method) => (
                      <MenuItem key={method} value={method}>
                        {ASSIGNMENT_RULE_METHOD_DICT[method]['desc']}
                      </MenuItem>
                    ))}
                  </TextFieldFormik>
                </div>
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={12}>
                <RuleConditionsQueryBuilder
                  entity={values.entity}
                  lob={values.lob}
                  isAllSubOrgs={values.is_all_sub_orgs}
                  subOrgIds={values.sub_organization_ids}
                  onFieldsLoaded={setConditionFields}
                  onLoadingChange={setIsLoadingConditionFields}
                />
              </Grid>
              <Grid item xs={6}>
                <div>
                  <RuleAssigneeSelect id={values.id} entity={values.entity} method={values.method} />
                </div>
              </Grid>
              <Grid item xs={6} />
            </Grid>
            <div className={classes.buttonsContainer}>
              <CancelButton disabled={isSubmitting} onClick={onClose} />
              <SaveRuleButton
                isWaiting={isSubmitting || isLoadingConditionFields}
                onClick={handleSubmit}
                // Every other validation failure is fixable inside the row that caused it, so
                // the QB's own red indicators are enough and Save stays live. A field the
                // catalog no longer serves is not: the row is read-only by then, and the
                // remedies are the rule's scope or the delete icon. Leaving Save clickable
                // there means a click that does nothing at all.
                blockedReason={
                  hasUnknownFieldPath(values.conditions_json, conditionFields) ? UNKNOWN_FIELD_SAVE_BLOCKED : undefined
                }
              />
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

EditAssignmentRuleDialog.propTypes = {
  rule: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  entity: PropTypes.oneOf(['claims', 'exposures']).isRequired,
  existingRuleNames: PropTypes.array,
};

const RuleAssigneeSelect = ({ id, method, entity }) => {
  const LABEL = 'Assignee';
  const { classes } = useStyles();
  const { organization } = useSysconfig();
  const { values, setFieldValue } = useFormikContext();
  const [previousParams, setPreviousParams] = useState(null);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [isLoadingAssigneeOptions, setIsLoadingAssigneeOptions] = useState(false);
  const [initialLoadOptionsDone, setInitialLoadOptionsDone] = useState(false);

  // Depend on the tag's primitives, not the object: a re-created `{kind, id}` would otherwise
  // churn the effect's identity and re-fire the fetch before `previousParams` is set.
  const assigneeKind = values.assignee?.kind;
  const assigneeId = values.assignee?.id ?? null;
  const matchesAssignee = (option) => option.kind === assigneeKind && (option.id ?? null) === assigneeId;

  useEffect(() => {
    (async () => {
      const params = {
        method,
        entity,
      };
      if (method && entity && JSON.stringify(params) !== JSON.stringify(previousParams)) {
        setIsLoadingAssigneeOptions(true);
        try {
          const { data } = await axios.get(
            `/sysconfig/api/v1/organizations/${organization.id}/assignments/rules/assignee_options`,
            {
              params,
            }
          );

          setPreviousParams(params);

          if (assigneeKind && !data.find((o) => o.kind === assigneeKind && (o.id ?? null) === assigneeId)) {
            setFieldValue('assignee', null);
          }

          setAssigneeOptions(data);
          setInitialLoadOptionsDone(true);
        } catch (error) {
          await reportAxiosError(error);
        } finally {
          setIsLoadingAssigneeOptions(false);
        }
      }
    })();
  }, [organization.id, method, entity, assigneeKind, assigneeId, setFieldValue, previousParams]);

  const props = {
    label: LABEL,
    className: classes.formTextField,
    fullWidth: true,
  };

  if (id && !initialLoadOptionsDone) {
    // Wait for the loaded results - otherwise there'll be an error that the value is not an option
    return <TextField {...props} disabled />;
  }

  // MUI selects on a primitive, so the field renders the chosen option's `key` while the Formik
  // value stays the tagged `{kind, id}` the API expects.
  const handleAssigneeChange = (event) => {
    const option = assigneeOptions.find((o) => o.key === event.target.value);
    setFieldValue('assignee', option ? { kind: option.kind, id: option.id ?? null } : null);
  };

  return (
    <>
      <TextFieldFormik
        id="assignee"
        {...props}
        select
        value={assigneeOptions.find(matchesAssignee)?.key ?? ''}
        onChange={handleAssigneeChange}
        disabled={isLoadingAssigneeOptions || !assigneeOptions.length || !entity || !method}
        className={classes.formTextFieldNoErrorSpacing}
      >
        {assigneeOptions.map((o) => (
          <MenuItem key={o.key} value={o.key}>
            {o.display_name}
          </MenuItem>
        ))}
      </TextFieldFormik>
      {entity && method === 'round_robin' && assigneeOptions.length === 0 && (
        <Typography className={classes.formUnderInputErrorText}>
          Please create a group to choose an assignee for round robin method
        </Typography>
      )}
    </>
  );
};

RuleAssigneeSelect.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entity: PropTypes.string,
  method: PropTypes.string,
};

const AssignmentGroupsTab = () => {
  const { organization } = useSysconfig();
  const { classes } = useStyles();
  const {
    isLoading,
    isError,
    data: groups,
    reloadData: reloadGroups,
  } = useDataFetcher(`/sysconfig/api/v1/organizations/${organization.id}/assignments/groups`);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);

  if (isLoading || isError) {
    return <LoadingIndicator isError={isError} />;
  }

  const getMixpanelEventParams = (values) => {
    return {
      group_name: values?.display_name,
      users: values?.user_ids,
    };
  };

  const addGroup = () => {
    setGroupDialogOpen(true);

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ADD_ASSIGNMENT_GROUP_CLICKED);
  };

  const editGroup = (group) => {
    setGroupToEdit(group);
    setGroupDialogOpen(true);

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.EDIT_ASSIGNMENT_GROUP_CLICKED, getMixpanelEventParams(group));
  };

  const onClose = () => {
    setGroupToEdit(null);
    setGroupDialogOpen(false);
  };

  const onDeleteGroup = async (group) => {
    try {
      await axios.delete(`/sysconfig/api/v1/organizations/${organization.id}/assignments/groups/${group.id}`, {
        params: { deleteRules: true },
      });

      mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_GROUP_DELETED, getMixpanelEventParams(group));
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const onCreateGroup = async (values) => {
    await axios.post(`/sysconfig/api/v1/organizations/${organization.id}/assignments/groups`, values);
    await reloadGroups();

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_GROUP_ADDED, getMixpanelEventParams(values));
  };

  const onUpdateGroup = async (values, id) => {
    await axios.patch(`/sysconfig/api/v1/organizations/${organization.id}/assignments/groups/${id}`, values);
    await reloadGroups();

    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.ASSIGNMENT_GROUP_EDITED, getMixpanelEventParams(values));
  };

  const handleOpenDeleteConfirmModal = (group) => {
    mixpanel.track(ASSIGNMENTS_MIXPANEL_EVENTS.DELETE_ASSIGNMENT_GROUP_CLICKED, getMixpanelEventParams(group));
  };

  const columnData = [
    { id: 'display_name', label: 'Group Name' },
    {
      id: 'connected_rules',
      label: 'Connected Rules',
      specialCell: (group) => (
        <>
          {group.rule_names.length > 5 ? (
            <Tooltip title={group.rule_names.join(', ')}>
              <span>{group.rule_names.length} Rules</span>
            </Tooltip>
          ) : (
            group.rule_names.join(', ')
          )}
        </>
      ),
    },
    {
      id: 'actions',
      disableSort: true,
      label: 'Actions',
      width: '70px',
      specialCell: (group) => (
        <>
          <span style={{ marginRight: 10 }}>
            <InlineIconButton
              icon={PencilIcon}
              className={classes.hoverableNonFilledIcon}
              onClick={() => editGroup(group)}
            />
          </span>
          <WithConfirm
            title="Delete Group?"
            contentText={`Are you sure you want to delete "${group.display_name}"?${
              group.rule_names.length > 0 ? ' This action will delete all associated rules as well.' : ''
            }`}
            primaryButtonName="Delete"
            postOnClick={async () => await reloadGroups()}
            onOpenConfirmModal={() => handleOpenDeleteConfirmModal(group)}
          >
            <InlineIconButton
              icon={TrashIcon_Deprecated}
              className={classes.hoverableIcon}
              onClick={async () => await onDeleteGroup(group)}
            />
          </WithConfirm>
        </>
      ),
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <Button color="primary" onClick={addGroup} style={{ float: 'right', marginBottom: 14 }}>
          <AddIcon />
          Add Group
        </Button>
      </Grid>
      <Grid item xs={12}>
        <SortableTable columns={columnData} rows={groups} />
      </Grid>
      {groupDialogOpen && (
        <EditAssignmentGroupDialog
          onSubmit={groupToEdit ? onUpdateGroup : onCreateGroup}
          onClose={onClose}
          group={groupToEdit}
          existingGroupNames={groups.filter((group) => group.id !== groupToEdit?.id).map((group) => group.display_name)}
          existingGroupKeys={groups
            .filter((group) => group.id !== groupToEdit?.id)
            .map((group) => group.assignment_group_key)}
        />
      )}
    </Grid>
  );
};

const GroupKeyField = ({ group }) => {
  const { isSubmitting, values } = useFormikContext();
  const { classes } = useStyles();

  useSetDefaultFieldsOnChange(getIn(values, 'display_name'), {
    assignment_group_key: group ? getIn(values, 'assignment_group_key') : snakeCase(getIn(values, 'display_name')),
  });

  return (
    <div>
      <TextFieldFormik
        id="assignment_group_key"
        label="Group Key"
        className={classes.formTextField}
        fullWidth
        disabled={isSubmitting || !!group}
      />
    </div>
  );
};

GroupKeyField.propTypes = {
  group: PropTypes.object,
};

const EditAssignmentGroupDialog = ({ group, onSubmit, onClose, existingGroupNames = [], existingGroupKeys = [] }) => {
  const { classes } = useStyles();

  const { adjustersDict } = useAssignments();

  return (
    <Formik
      initialValues={{
        assignment_group_key: group?.assignment_group_key || '',
        display_name: group?.display_name || '',
        user_ids: group
          ? _.chain(group.user_ids)
              .sortBy([(user_id) => adjustersDict[user_id].username])
              .value()
          : [],
      }}
      validationSchema={Yup.object().shape({
        assignment_group_key: Yup.string()
          .required('Required')
          .trim()
          .matches(/^[a-z0-9_]+$/, 'Can only contain lowercase letters, numbers and underscores')
          .notOneOf(existingGroupKeys, 'Must be unique'),
        display_name: Yup.string().required('Required').notOneOf(existingGroupNames, 'Must be unique'),
        user_ids: Yup.array().required('Required').min(1, 'Required'),
      })}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values, group?.id);
          await onClose();
        } catch (error) {
          await reportAxiosError(error);
          formikProps.setSubmitting(false);
        }
      }}
    >
      {(formikProps) => {
        const { isSubmitting, handleSubmit, values, setFieldValue } = formikProps;

        const removeUser = (index) => {
          const ids = values.user_ids;
          setFieldValue('user_ids', [...ids.slice(0, index), ...ids.slice(index + 1, ids.length)]);
        };

        return (
          <CardDialog
            isDialog
            title={group ? 'Edit Group' : 'Add Group'}
            fullWidth
            maxWidth="xs"
            onClose={onClose}
            preventClose={isSubmitting}
          >
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <div>
                  <TextFieldFormik id="display_name" label="Group Name" className={classes.formTextField} fullWidth />
                </div>
              </Grid>
              <Grid item xs={8}>
                <GroupKeyField group={group} />
              </Grid>
              <Grid item xs={8}>
                <div>
                  <MultiSelectTextFieldFormik
                    id="user_ids"
                    label="Users"
                    options={Object.keys(adjustersDict)}
                    renderValue={() => ''}
                    renderOption={(user_id) => adjustersDict[user_id].username}
                    className={classes.formTextFieldNoErrorSpacing}
                    fullWidth
                  />
                </div>
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={12}>
                {values.user_ids.map((user_id, index) => (
                  <Chip
                    key={user_id}
                    className={classes.chip}
                    label={
                      <OverflowTextWithToolTip maxWidth="220px">
                        {adjustersDict[user_id].username}
                        <IconButton style={{ padding: 4, fontSize: 5 }} title="Clear" onClick={() => removeUser(index)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </OverflowTextWithToolTip>
                    }
                  />
                ))}
              </Grid>
            </Grid>
            <div className={classes.buttonsContainer}>
              <CancelButton disabled={isSubmitting} onClick={onClose} />
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                Save
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

EditAssignmentGroupDialog.propTypes = {
  group: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  existingGroupNames: PropTypes.array,
  existingGroupKeys: PropTypes.array,
};

export default AssignmentsTab;
